import React, { useEffect, useState } from "react";
import { Badge, Card, Col, Container, Form, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { MessageLogService } from "Services/messageLogService";
import TablePagination from "components/Pagination/Table.paginaition";

interface MessageLog {
  _id: string;
  channel: string;
  status: string;
  to: string;
  subject?: string;
  templateId?: string;
  mailType?: string;
  error?: string;
  createdAt: string;
}

export default function MessageLogs() {
  const [logs, setLogs] = useState<MessageLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [channelFilter, setChannelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTo, setSearchTo] = useState("");

  const fetchLogs = async () => {
    try {
      const res = await MessageLogService.getLogs({
        page,
        limit: perPage,
        channel: channelFilter || undefined,
        status: statusFilter || undefined,
        to: searchTo || undefined,
      });
      if (res?.status === 200) {
        setLogs(res.data.data.logs);
        setTotal(res.data.data.total);
      }
    } catch (err) {
      toast.error("Failed to fetch message logs");
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, perPage, channelFilter, statusFilter]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchLogs();
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTo]);

  return (
    <Container>
      <div className="py-4">
        <h5 className="mb-3">Message Logs</h5>

        <Row className="mb-3 g-2">
          <Col md={3}>
            <Form.Control
              type="text"
              placeholder="Search by recipient..."
              value={searchTo}
              onChange={(e) => setSearchTo(e.target.value)}
              size="sm"
            />
          </Col>
          <Col md={2}>
            <Form.Select
              size="sm"
              value={channelFilter}
              onChange={(e) => { setChannelFilter(e.target.value); setPage(1); }}
            >
              <option value="">All Channels</option>
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Select
              size="sm"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </Form.Select>
          </Col>
        </Row>

        <Card>
          <Card.Body className="p-0">
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <th className="fs-12">#</th>
                  <th className="fs-12">Channel</th>
                  <th className="fs-12">Status</th>
                  <th className="fs-12">To</th>
                  <th className="fs-12">Subject / Template</th>
                  <th className="fs-12">Type</th>
                  <th className="fs-12">Error</th>
                  <th className="fs-12">Date</th>
                </tr>
              </thead>
              <tbody>
                {logs.length > 0 ? (
                  logs.map((log, index) => (
                    <tr key={log._id}>
                      <td className="fs-12">{(page - 1) * perPage + index + 1}</td>
                      <td className="fs-12">
                        <Badge bg={log.channel === "email" ? "primary" : "success"}>
                          {log.channel}
                        </Badge>
                      </td>
                      <td className="fs-12">
                        <Badge bg={log.status === "success" ? "success" : "danger"}>
                          {log.status}
                        </Badge>
                      </td>
                      <td className="fs-12">{log.to}</td>
                      <td className="fs-12">{log.subject || log.templateId || "-"}</td>
                      <td className="fs-12">{log.mailType || "-"}</td>
                      <td className="fs-12 text-truncate" style={{ maxWidth: 200 }}>
                        {log.error || "-"}
                      </td>
                      <td className="fs-12">{new Date(log.createdAt).toLocaleString()}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center py-3">
                      No logs found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {total > 0 && (
          <div className="mt-3">
            <TablePagination
              total={total}
              currentPage={page}
              perPage={perPage}
              handlePageChange={setPage}
              setPerPage={setPerPage}
            />
          </div>
        )}
      </div>
    </Container>
  );
}
